import { useLocalStore } from 'mobx-react-lite';
import { FC, useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps, PopupStore } from '@gem/popups';
import { platformInfo } from '@gem/utils';

import Tasks from 'APP/Tasks';
import {
  DeviceStatus,
  IUserDevice,
  IUserDevices,
} from 'APP/model/userSettings/userSettingsModel.types';
import Entities from 'STORE';

import { SessionsItemDetailsPopup } from '../SessionsItemDetailsPopup/SessionsItemDetailsPopup';

interface ISessionsPopupPresenter {
  childPopup: PopupStore | null;
  sessions: [string, IUserDevices][];
  currentSession: IUserDevice;
  hasBackButton: boolean;
  isLoading: boolean;
  isLoadingTerminateAll: boolean;
  hasTerminateAll: boolean;
  onClose(): void;
  onBack(): void;
  onOpenSession(sessionKey: string): void;
  openChildPopup<T>(Component: FC<ICommonPopupsProps<T>>, params?: T): void;
  onTerminateAllSessions(): Promise<void>;
}

const mapSessionStatusToTitle: { [key: string]: string } = {
  [DeviceStatus.Trusted]: 'common_active_sessions',
  [DeviceStatus.Pending]: 'trusted_device_pending_devices',
  [DeviceStatus.Unlocked]: 'trusted_device_pending_devices',
  [DeviceStatus.Blocked]: 'trusted_device_blocked_devices',
};

export function useSessionsPopupPresenter({
  popupInstance,
  onClose,
  onBack,
}: ICommonPopupsProps): ISessionsPopupPresenter {
  const { t } = useTranslation();

  const presenter = useLocalStore<ISessionsPopupPresenter>(() => ({
    childPopup: null,
    isLoadingTerminateAll: false,

    get sessions(): [string, IUserDevices][] {
      const sortedSessions = Entities.sessionsStore.sessions.sort((a, b) => {
        if (a.lastActivityTs < b.lastActivityTs) return 1;
        if (a.lastActivityTs > b.lastActivityTs) return -1;
        return 0;
      });

      const sessionsObj: { [key: string]: IUserDevices } = {
        [mapSessionStatusToTitle[DeviceStatus.Trusted]]: [],
        [mapSessionStatusToTitle[DeviceStatus.Pending]]: [],
        [mapSessionStatusToTitle[DeviceStatus.Blocked]]: [],
      };

      sortedSessions.forEach((item) => {
        const title = item.device.status ? mapSessionStatusToTitle[item.device.status] : null;

        if (!title) return;

        sessionsObj[title].push(item);
      });

      return Object.entries(sessionsObj).filter((entries) => entries[1].length);
    },

    get isLoading(): boolean {
      return !Entities.sessionsStore.loaded;
    },

    get currentSession(): IUserDevice {
      return Entities.sessionsStore.currentSession;
    },

    get hasBackButton(): boolean {
      return !!onBack;
    },

    get hasTerminateAll(): boolean {
      return Entities.sessionsStore.activeSessions.length > 0;
    },

    onClose(): void {
      popupInstance.close();
      onClose?.();
    },

    onBack(): void {
      popupInstance.close();
      onBack?.();
    },

    onOpenSession(sessionKey: string): void {
      presenter.openChildPopup(SessionsItemDetailsPopup, { sessionKey });
    },

    openChildPopup<T>(Component: FC<ICommonPopupsProps<T>>, params?: T): void {
      popupInstance.hide();

      presenter.childPopup = Tasks.app.openPopup(Component, {
        params,
        onClose: () => presenter.onClose,
        onBack: () => popupInstance.show(),
      });
    },

    async onTerminateAllSessions(): Promise<void> {
      if (Entities.sessionsStore.sessions.length) {
        presenter.isLoadingTerminateAll = true;
        const confirmation = await Tasks.app.showConfirm({
          title: t('trusted_device_block_all_other_devices_confirm_title'),
          text: t('trusted_device_block_all_other_devices_confirm_description'),
        });

        if (!confirmation) {
          presenter.isLoadingTerminateAll = false;
          return;
        }
        const devicesIds = Entities.sessionsStore.activeSessions
          .filter((item) => item.device.deviceId !== platformInfo.deviceId)
          .map((session) => session.device.deviceId);
        const response = await Tasks.userSettings.terminateOrBlockSessions(
          devicesIds,
          DeviceStatus.Blocked
        );

        if (response) {
          Entities.sessionsStore.removeSessions(devicesIds);
        } else {
          Tasks.app.showToast(t('something_went_wrong'));
        }
        presenter.isLoadingTerminateAll = false;
      }
    },
  }));

  useEffect(() => {
    return () => presenter.childPopup?.close();
  }, []);

  return presenter;
}
